import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2, Phone, Mail, Calendar, User } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Alert, AlertDescription } from '../../ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  getStageData,
  completeStage,
  addContact,
  getContacts
} from '../../../api/claims';

const CustomerContactModal = ({ open, onOpenChange, claimId, onComplete }) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    contact_title: '',
    contact_date: new Date().toISOString().split('T')[0],
    contact_method: 'email',
    initial_response_status: 'pending',
    communication_notes: ''
  });

  // Fetch stage data
  const { data: stageDataResponse, isLoading: stageLoading } = useQuery({
    queryKey: ['stageData', claimId, 'customer_contact'],
    queryFn: () => getStageData(claimId, 'customer_contact'),
    enabled: open && !!claimId
  });

  // Fetch contacts
  const { data: contactsResponse, isLoading: contactsLoading } = useQuery({
    queryKey: ['contacts', claimId],
    queryFn: () => getContacts(claimId),
    enabled: open && !!claimId
  });

  const stageData = stageDataResponse?.data || {};
  const contacts = contactsResponse?.data || [];

  // Add contact mutation
  const addContactMutation = useMutation({
    mutationFn: (data) => addContact(claimId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['contacts', claimId]);
      setFormData({
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        contact_title: '',
        contact_date: new Date().toISOString().split('T')[0],
        contact_method: 'email',
        initial_response_status: 'pending',
        communication_notes: ''
      });
    }
  });

  // Complete mutation
  const completeMutation = useMutation({
    mutationFn: () => completeStage(claimId, 'customer_contact'),
    onSuccess: () => {
      queryClient.invalidateQueries(['stageData', claimId]);
      queryClient.invalidateQueries(['claimDetail', claimId]);
      if (onComplete) onComplete();
      onOpenChange(false);
    }
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddContact = () => {
    if (formData.contact_name && formData.contact_date && formData.contact_method) {
      addContactMutation.mutate(formData);
    }
  };

  const handleComplete = () => {
    completeMutation.mutate();
  };

  const isLoading = stageLoading || contactsLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>Customer Contact</span>
            {stageData.is_completed && (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            )}
          </DialogTitle>
          <DialogDescription>
            Log initial communication with the OEM regarding this claim.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Existing Contacts */}
            {contacts.length > 0 && (
              <div className="space-y-3">
                <Label>Contact History</Label>
                <div className="space-y-2">
                  {contacts.map((contact) => (
                    <div
                      key={contact.id}
                      className="p-3 bg-gray-50 rounded-lg space-y-2"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">{contact.contact_name}</p>
                          {contact.contact_title && (
                            <p className="text-sm text-gray-600">{contact.contact_title}</p>
                          )}
                        </div>
                        <span className="text-xs text-gray-500">
                          {new Date(contact.contact_date).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex gap-4 text-sm text-gray-600">
                        {contact.contact_email && (
                          <div className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {contact.contact_email}
                          </div>
                        )}
                        {contact.contact_phone && (
                          <div className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {contact.contact_phone}
                          </div>
                        )}
                      </div>
                      {contact.communication_notes && (
                        <p className="text-sm text-gray-700 mt-2">{contact.communication_notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add New Contact */}
            {!stageData.is_completed && (
              <div className="space-y-4 pt-4 border-t">
                <Label>Add Contact Entry</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact-name">Contact Name *</Label>
                    <Input
                      id="contact-name"
                      value={formData.contact_name}
                      onChange={(e) => handleInputChange('contact_name', e.target.value)}
                      placeholder="John Smith"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-title">Title</Label>
                    <Input
                      id="contact-title"
                      value={formData.contact_title}
                      onChange={(e) => handleInputChange('contact_title', e.target.value)}
                      placeholder="Purchasing Manager"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-email">Email</Label>
                    <Input
                      id="contact-email"
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => handleInputChange('contact_email', e.target.value)}
                      placeholder="john@oem.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-phone">Phone</Label>
                    <Input
                      id="contact-phone"
                      value={formData.contact_phone}
                      onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                      placeholder="+1-555-1234"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-date">Contact Date *</Label>
                    <Input
                      id="contact-date"
                      type="date"
                      value={formData.contact_date}
                      onChange={(e) => handleInputChange('contact_date', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-method">Method *</Label>
                    <Select
                      value={formData.contact_method}
                      onValueChange={(value) => handleInputChange('contact_method', value)}
                    >
                      <SelectTrigger id="contact-method">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="phone">Phone</SelectItem>
                        <SelectItem value="meeting">In-Person Meeting</SelectItem>
                        <SelectItem value="video_call">Video Call</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="response-status">Response Status</Label>
                    <Select
                      value={formData.initial_response_status}
                      onValueChange={(value) => handleInputChange('initial_response_status', value)}
                    >
                      <SelectTrigger id="response-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="acknowledged">Acknowledged</SelectItem>
                        <SelectItem value="investigating">Investigating</SelectItem>
                        <SelectItem value="disputed">Disputed</SelectItem>
                        <SelectItem value="accepted">Accepted</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="communication-notes">Communication Notes</Label>
                  <Textarea
                    id="communication-notes"
                    value={formData.communication_notes}
                    onChange={(e) => handleInputChange('communication_notes', e.target.value)}
                    placeholder="Details of the communication..."
                    rows={3}
                  />
                </div>
                <Button
                  onClick={handleAddContact}
                  disabled={!formData.contact_name || addContactMutation.isPending}
                  className="w-full"
                >
                  {addContactMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    'Add Contact Entry'
                  )}
                </Button>
              </div>
            )}

            {stageData.is_completed && (
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  This stage was completed on {new Date(stageData.completed_at).toLocaleDateString()}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {!stageData.is_completed && contacts.length > 0 && (
            <Button
              onClick={handleComplete}
              disabled={completeMutation.isPending}
            >
              {completeMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Completing...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Mark Complete
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerContactModal;